import { useEffect, useState, useCallback } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { LogOut, ShoppingBag, Backpack, Coins, Flame, Trophy, Target, BookOpen, Award } from "lucide-react";
import { api, absUrl } from "@/lib/api";
import { initTypewriterSfx, playTypewriterTick } from "@/lib/typewriterSfx";
import { playContextualSfxForTurn } from "@/lib/sfx";
import DualMediaPanel from "@/components/game/DualMediaPanel";
import AdaptiveAudio from "@/components/game/AdaptiveAudio";
import StatsHUD from "@/components/game/StatsHUD";
import NarrativeBlock from "@/components/game/NarrativeBlock";
import ActionPanel from "@/components/game/ActionPanel";
import ClassSelection from "@/components/game/ClassSelection";
import GameOverScreen from "@/components/game/GameOverScreen";
import VictoryScreen from "@/components/game/VictoryScreen";
import ChapterBanner from "@/components/game/ChapterBanner";
import StoreModal from "@/components/game/StoreModal";
import ShopModal from "@/components/game/ShopModal";
import WellOfSoulsModal from "@/components/game/WellOfSoulsModal";
import TurnRewards from "@/components/game/TurnRewards";
import CombatPanel from "@/components/game/CombatPanel";
import CombatLog from "@/components/game/CombatLog";
import CodexModal from "@/components/game/CodexModal";
import DailyQuestsModal from "@/components/game/DailyQuestsModal";
import AchievementsModal from "@/components/game/AchievementsModal";
import LivesExhaustedModal from "@/components/game/LivesExhaustedModal";
import AshenThresholdModal from "@/components/game/AshenThresholdModal";
import StarterPackModal from "@/components/game/StarterPackModal";
import BossCutin from "@/components/game/BossCutin";
import PartyBar from "@/components/game/PartyBar";
import EventBanner from "@/components/game/EventBanner";
import DailyEmberButton from "@/components/game/DailyEmberButton";
import OnboardingOverlay from "@/components/game/OnboardingOverlay";
import BossDefeatReviveModal from "@/components/game/BossDefeatReviveModal";
import PushNotifyButton from "@/components/game/PushNotifyButton";
import HpRestoreAdButton from "@/components/game/HpRestoreAdButton";
import BossLootChestModal from "@/components/game/BossLootChestModal";
import DeathCinematic from "@/components/game/DeathCinematic";
import AchievementUnlockToast from "@/components/game/AchievementUnlockToast";
import ComboMeter from "@/components/game/ComboMeter";
import EnrageOverlay from "@/components/game/EnrageOverlay";
import MetaShopModal from "@/components/game/MetaShopModal";
import PerkDraftModal from "@/components/game/PerkDraftModal";
import BossPreviewCard from "@/components/game/BossPreviewCard";
import ActivePerksBadge from "@/components/game/ActivePerksBadge";
import ActiveSetsBadge from "@/components/game/ActiveSetsBadge";
import FirstDeathRescueBanner from "@/components/game/FirstDeathRescueBanner";
import LowHpPotionNudge from "@/components/game/LowHpPotionNudge";
import { initH5Ads } from "@/lib/h5ads";
import { stopAdFrequency } from "@/lib/adFrequency";
import { INTENDED_CLASS_KEY, INTENDED_UTM_KEY } from "@/pages/CardPage";

const MENU_TRACK = "https://incompetech.com/music/royalty-free/mp3-royaltyfree/Dark%20Fog.mp3";

export default function DashboardPage() {
  const navigate = useNavigate();
  const [state, setState] = useState(null);        // full backend state (user, lives, active_run, classes)
  const [run, setRun] = useState(null);            // current active run
  const [busy, setBusy] = useState(false);
  const [thinking, setThinking] = useState(false); // AI in progress
  const [gameOver, setGameOver] = useState(null);
  const [victory, setVictory] = useState(null);
  const [storeOpen, setStoreOpen] = useState(false);
  const [shopOpen, setShopOpen] = useState(false);
  const [livesModalOpen, setLivesModalOpen] = useState(false);
  const [ashenOpen, setAshenOpen] = useState(false);
  const [starterPackOpen, setStarterPackOpen] = useState(false);
  const [starterPackContext, setStarterPackContext] = useState({ floor: 0, kills: 0 });
  const [onboardingOpen, setOnboardingOpen] = useState(false);
  const [bossDefeatOpen, setBossDefeatOpen] = useState(false);
  const [lootChestOpen, setLootChestOpen] = useState(false);
  const [lastBossKillSeen, setLastBossKillSeen] = useState(null);
  const [deathCinematic, setDeathCinematic] = useState(null); // { portraitUrl, className, pendingRun } | null
  const [trophyQueue, setTrophyQueue] = useState([]);          // FIFO of newly-unlocked achievements to toast
  const [metaShopOpen, setMetaShopOpen] = useState(false);
  // iter67 — in-run perk draft + boss preview card
  const [perkDraftOpen, setPerkDraftOpen] = useState(false);
  const [bossPreview, setBossPreview] = useState(null);        // { name, floor, preview_hp, preview_dmg, forge_cta } | null
  const [seenPreviewFloors, setSeenPreviewFloors] = useState(new Set());
  // iter67.5 — First-Death Rescue banner (fires ONCE per user lifetime).
  const [rescueBanner, setRescueBanner] = useState(null);

  // HP-low preload: when the player's HP drops below 30%, warm the H5 ad SDK
  // so a death-screen ad request has a pre-cached slot ready. Fire-once per
  // low-HP window — resets when HP climbs back above 50%.
  const [lowHpWarmed, setLowHpWarmed] = useState(false);
  useEffect(() => {
    const hp  = run?.health ?? 0;
    const max = run?.max_health ?? 100;
    const ratio = max > 0 ? hp / max : 1;
    if (ratio < 0.30 && !lowHpWarmed) {
      initH5Ads();  // idempotent — triggers preloadAdBreaks:'on' refresh
      setLowHpWarmed(true);
    } else if (ratio > 0.50 && lowHpWarmed) {
      setLowHpWarmed(false);
    }
  }, [run?.health, run?.max_health, lowHpWarmed]);

  // No ads during gameplay — ads only run inside a "Watch Ad" break.
  useEffect(() => { stopAdFrequency(); }, []);

  // A Monetag tag keeps serving pop-ups until the page unloads, so once the
  // player is back in a live run after an ad break, reload to purge it.
  useEffect(() => {
    if (run && !run.is_dead && window.__adTagDirty) window.location.reload();
  }, [run]);

  // iter67 — Open perk draft when backend offers one (pending_perks arrives from SSE 'done').
  useEffect(() => {
    const pp = run?.pending_perks;
    if (pp && pp.length > 0 && !perkDraftOpen) {
      setPerkDraftOpen(true);
    }
  }, [run?.pending_perks, perkDraftOpen]);

  // iter67 — Open boss-preview card when backend emits one on floor advance.
  useEffect(() => {
    const bp = run?.boss_preview;
    if (bp && bp.floor && !seenPreviewFloors.has(bp.floor)) {
      setBossPreview(bp);
      setSeenPreviewFloors((prev) => {
        const n = new Set(prev); n.add(bp.floor); return n;
      });
    }
  }, [run?.boss_preview, seenPreviewFloors]);
  const [combatLog, setCombatLog] = useState([]);  // last combat-round entries for CombatLog
  const [codexOpen, setCodexOpen] = useState(false);
  const [questsOpen, setQuestsOpen] = useState(false);
  const [trophiesOpen, setTrophiesOpen] = useState(false);
  const [bossCutin, setBossCutin] = useState(null); // { key, name, portrait } when a new boss reveal fires
  const [wellOpen, setWellOpen] = useState(false);
  const [streamingText, setStreamingText] = useState("");   // live narrative while SSE streams
  const [strikeKey, setStrikeKey] = useState(0);            // increments to retrigger combat strike anim
  const [hitKey, setHitKey] = useState(0);                  // increments when player takes damage

  const refreshState = useCallback(async () => {
    try {
      const s = await api.get("/game/state");
      setState(s);
      setRun(s.active_run || null);
      // Onboarding: fire ONCE on the user's very first run, on turn 0 or 1.
      // Backend flips tutorial_completed=true permanently on completion/skip so
      // this branch stays false forever after.
      if (
        s.active_run &&
        (s.active_run.turn ?? 0) <= 1 &&
        !s.tutorial_completed &&
        !s.active_run.is_dead
      ) {
        setOnboardingOpen(true);
      }
      // Trophy inbox: fetch newly-unlocked achievements + queue them for toast.
      // Silent-fail — a dead inbox call shouldn't break the state refresh.
      try {
        const inbox = await api.get("/achievements/inbox");
        if (inbox?.trophies?.length) {
          setTrophyQueue((q) => [...q, ...inbox.trophies]);
        }
      } catch { /* noop */ }
      // iter67.5 — Daily Login Streak toast (first state fetch of the UTC day only).
      if (s?.login_streak_bonus?.day) {
        const b = s.login_streak_bonus;
        toast.success(
          `${b.message}`,
          { duration: 5500, id: `login-streak-${b.day}` }
        );
      }
      return s;
    } catch (e) {
      if (e?.response?.status === 401) navigate("/", { replace: true });
      throw e;
    }
  }, [navigate]);

  useEffect(() => { refreshState(); }, [refreshState]);

  // Party co-op: poll state every 3s while in a party so partner turns arrive quickly.
  useEffect(() => {
    if (!state?.party || state.party.status !== "playing") return;
    if (!state.party.guest_user_id) return;
    const t = setInterval(() => { refreshState(); }, 3000);
    return () => clearInterval(t);
  }, [state?.party?.status, state?.party?.guest_user_id, refreshState]);

  // If the user arrived from a public /card/:id CTA, auto-start a run with the intended class.
  useEffect(() => {
    if (!state) return;
    if (state.active_run) { try { sessionStorage.removeItem(INTENDED_CLASS_KEY); } catch { /* noop */ } return; }
    let intended = null;
    try { intended = sessionStorage.getItem(INTENDED_CLASS_KEY); } catch { /* noop */ }
    if (!intended) return;
    if (!state.classes?.[intended]) {
      try { sessionStorage.removeItem(INTENDED_CLASS_KEY); } catch { /* noop */ }
      return;
    }
    if ((state.lives_remaining + (state.bonus_lives || 0)) <= 0) {
      try { sessionStorage.removeItem(INTENDED_CLASS_KEY); } catch { /* noop */ }
      toast.error("No embers left today. The realm asks you to come back at dawn.");
      return;
    }
    try { sessionStorage.removeItem(INTENDED_CLASS_KEY); } catch { /* noop */ }
    toast.info(`Taking up the ${state.classes[intended].name}'s mantle...`);
    startRun(intended);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state]);

  // Handle return from Stripe Checkout: poll status until paid/expired, then refresh.
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const sessionId = params.get("session_id");
    const cancelled = params.get("stripe") === "cancel";

    // Clean the URL immediately so we don't re-trigger on remounts
    if (sessionId || cancelled) {
      window.history.replaceState({}, "", "/dashboard");
    }
    if (cancelled) {
      toast.error("Checkout cancelled. No charges were made.");
      return;
    }
    if (!sessionId) return;

    let cancelledPoll = false;
    const toastId = toast.loading("Confirming payment with Stripe...");
    const poll = async (attempt = 0) => {
      if (cancelledPoll) return;
      if (attempt >= 8) {
        toast.error("Payment status timed out. Refresh in a moment or check your email.", { id: toastId });
        return;
      }
      try {
        const res = await api.get(`/payments/status/${sessionId}`);
        if (res.payment_status === "paid") {
          const gr = res.grant_result || {};
          let msg = "Payment confirmed.";
          if (res.kind === "lives_pack") msg = `+${gr.lives_added} embers added to your fire.`;
          else if (res.kind === "subscription") msg = "The Ember accepts your patronage. Unlimited descent unlocked.";
          else if (res.kind === "second_wind") msg = "A pact is struck. You breathe again.";
          else if (res.kind === "starter_pack") msg = `Starter Pact sealed. +${gr.lives_added || 3} embers and a rare weapon await your next descent.`;
          else if (res.kind === "season_pass") msg = "Season Pass claimed. +2 lives and +10% XP are yours until the next moon.";
          toast.success(msg, { id: toastId });
          setGameOver(null);
          setVictory(null);
          await refreshState();
          return;
        }
        if (res.status === "expired") {
          toast.error("Checkout session expired.", { id: toastId });
          return;
        }
      } catch (e) {
        // ignore, retry
      }
      setTimeout(() => poll(attempt + 1), 2000);
    };
    poll();

    return () => { cancelledPoll = true; };
  }, [refreshState]);

  // When combat begins but the enemy portrait hasn't landed yet, poll /api/game/state
  // every 1s (up to ~10s) to pick up the URL as soon as the background prewarm task finishes.
  useEffect(() => {
    if (!run) return;
    const needs = !!run.current_enemy && (run.enemy_hp || 0) > 0 && !run.current_enemy_portrait_url;
    if (!needs) return;
    let stopped = false;
    let attempts = 0;
    const tick = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const s = await api.get("/game/state");
        if (stopped) return;
        const portrait = s?.active_run?.current_enemy_portrait_url;
        if (portrait) {
          setRun((prev) => (prev ? { ...prev, current_enemy_portrait_url: portrait } : prev));
          return;
        }
      } catch { /* noop */ }
      if (attempts < 10) setTimeout(tick, 1000);
    };
    const t0 = setTimeout(tick, 1000);
    return () => { stopped = true; clearTimeout(t0); };
  }, [run?.current_enemy, run?.enemy_hp, run?.current_enemy_portrait_url]);

  // When the initial scene hasn't landed yet (start-run doesn't block on scene gen),
  // poll /api/game/state until current_scene_url diverges from the portrait fallback.
  useEffect(() => {
    if (!run) return;
    const stillOnFallback = !run.current_scene_url || run.current_scene_url === run.portrait_url;
    if (!stillOnFallback) return;
    let stopped = false;
    let attempts = 0;
    const tick = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const s = await api.get("/game/state");
        if (stopped) return;
        const sceneUrl = s?.active_run?.current_scene_url;
        if (sceneUrl && sceneUrl !== s?.active_run?.portrait_url) {
          setRun((prev) => (prev ? { ...prev, current_scene_url: sceneUrl } : prev));
          return;
        }
      } catch { /* noop */ }
      if (attempts < 8) setTimeout(tick, 1200);
    };
    const t0 = setTimeout(tick, 1200);
    return () => { stopped = true; clearTimeout(t0); };
  }, [run?.current_scene_url, run?.portrait_url]);

  // When the hero portrait didn't land at start-run (Nano Banana rate-limit),
  // poll /api/game/state until the background retry populates portrait_url.
  useEffect(() => {
    if (!run) return;
    if (run.portrait_url) return;
    let stopped = false;
    let attempts = 0;
    const tick = async () => {
      if (stopped) return;
      attempts += 1;
      try {
        const s = await api.get("/game/state");
        if (stopped) return;
        const p = s?.active_run?.portrait_url;
        if (p) {
          setRun((prev) => (prev ? { ...prev, portrait_url: p } : prev));
          return;
        }
      } catch { /* noop */ }
      // Retry window aligned with backend backoff (3s, 8s, 20s, 45s, 90s)
      if (attempts < 12) setTimeout(tick, 2500);
    };
    const t0 = setTimeout(tick, 2500);
    return () => { stopped = true; clearTimeout(t0); };
  }, [run?.portrait_url, run?.run_id]);


  const startRun = async (class_key) => {
    setBusy(true);
    setThinking(true);
    // Pick up any UTM attribution captured by the CardPage CTA
    let utm = {};
    try {
      const raw = sessionStorage.getItem(INTENDED_UTM_KEY);
      if (raw) { utm = JSON.parse(raw); sessionStorage.removeItem(INTENDED_UTM_KEY); }
    } catch { /* noop */ }
    try {
      const r = await api.post("/game/start-run", { class_key, ...utm });
      setRun(r);
      setGameOver(null);
      setVictory(null);
      await refreshState();
      toast.success(`You awaken as a ${r.class_name}.`);
      // iter67.6 — Streak milestone weapon granted (day 3/7). Celebrate loudly.
      if (r.streak_weapon_granted?.item_id) {
        toast.success(
          `🏆 STREAK REWARD · ${r.streak_weapon_granted.name} added to your inventory`,
          { duration: 6000 }
        );
      }
    } catch (e) {
      const msg = e?.response?.data?.detail || "Failed to start run";
      toast.error(msg);
    } finally {
      setBusy(false);
      setThinking(false);
    }
  };

  const takeAction = async ({ choice, custom_text }) => {
    if (!run || busy) return;
    setBusy(true);
    setThinking(true);
    setStreamingText("");
    // Remember what verb the player took, so the combat log can label the round correctly
    // when the SSE done arrives.
    setRun((prev) => (prev ? { ...prev, __last_action: choice || custom_text || "" } : prev));
    // Unlock AudioContext under this click gesture so typewriter ticks can play.
    initTypewriterSfx();
    try {
      const resp = await fetch(`${process.env.REACT_APP_BACKEND_URL}/api/game/action/stream`, {
        method: "POST",
        credentials: "include",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ choice, custom_text }),
      });
      if (!resp.ok || !resp.body) {
        const errText = await resp.text().catch(() => "");
        throw new Error(errText || `Stream failed (${resp.status})`);
      }

      // Optimistically clear the previous narrative so streaming text is the only thing visible.
      setRun((prev) => (prev ? { ...prev, current_narrative: "" } : prev));

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let buffer = "";
      let liveText = "";

      for (;;) {
        const { value, done } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });

        // SSE frames are separated by a blank line.
        let idx;
        while ((idx = buffer.indexOf("\n\n")) !== -1) {
          const frame = buffer.slice(0, idx);
          buffer = buffer.slice(idx + 2);
          let event = "message";
          const dataLines = [];
          for (const line of frame.split("\n")) {
            if (line.startsWith("event:")) event = line.slice(6).trim();
            else if (line.startsWith("data:")) dataLines.push(line.slice(5).trimStart());
          }
          if (!dataLines.length) continue;
          let data;
          try { data = JSON.parse(dataLines.join("\n")); }
          catch { continue; }

          if (event === "narrative_chunk") {
            liveText += data.text || "";
            // Once the first token arrives, hide the skeletons.
            setThinking(false);
            setStreamingText(liveText);
            playTypewriterTick();
          } else if (event === "state_update") {
            // If the enemy took damage this turn, retrigger the strike + floating damage animation.
            if ((data.last_enemy_hp_delta || 0) > 0) {
              setStrikeKey((k) => k + 1);
            }
            // If the player took damage this turn, shake the portrait + red vignette flash.
            if ((data.last_hp_delta || 0) < 0) {
              setHitKey((k) => k + 1);
            }
            setRun((prev) => (prev ? { ...prev, ...data } : prev));
          } else if (event === "choices") {
            setRun((prev) => (prev ? { ...prev, current_choices: data.choices || [] } : prev));
          } else if (event === "scene_image") {
            setRun((prev) => (prev ? { ...prev, current_scene_url: data.url } : prev));
          } else if (event === "done") {
            setRun((prev) => {
              // Boss cut-in trigger: brand-new boss encounter (first turn of the fight)
              // — detect by is_boss_room + fresh enemy_name + no prior boss reveal for this enemy.
              const prevEnemy = prev?.enemy_name || "";
              const newEnemy  = data?.enemy_name || "";
              const isFreshBoss =
                data?.is_boss_room &&
                newEnemy &&
                newEnemy !== prevEnemy &&
                (data?.enemy_hp === data?.enemy_max_hp || (data?.enemy_hp || 0) > 0);
              if (isFreshBoss) {
                setBossCutin({
                  key: `${data.run_id || prev?.run_id}-${newEnemy}-${data.turn}`,
                  name: newEnemy,
                  portrait: data.current_enemy_portrait_url || null,
                });
              }
              // ---- Combat Log — capture this round when combat happened ----
              // A "combat round" is one of:
              //   a) enemy HP dropped this turn (STRIKE / GUARD dealt damage), OR
              //   b) enemy died this turn (dead=true), OR
              //   c) player fled a combat (prev had enemy, now no enemy + minor HP hit)
              const prevEnemyHp = prev?.enemy_hp || 0;
              const nowEnemyHp  = data?.enemy_hp || 0;
              const dealt       = data?.last_enemy_hp_delta || 0;
              const taken       = data?.last_hp_delta < 0 ? -data.last_hp_delta : 0;
              const enemyKilled = prevEnemyHp > 0 && nowEnemyHp === 0 && prev?.current_enemy;
              const inCombat    = dealt > 0 || (data?.current_enemy && nowEnemyHp > 0);
              // Detect flee via the choice text the player just took (persisted in prev.__last_action)
              const lastAction  = (prev?.__last_action || "").toLowerCase();
              const fled = prevEnemyHp > 0 && nowEnemyHp === 0 && !enemyKilled &&
                           (lastAction.includes("flee") || lastAction.includes("retreat") ||
                            lastAction.includes("blink") || lastAction.includes("vanish") ||
                            lastAction.includes("turn undead"));
              if (dealt > 0 || enemyKilled || fled) {
                const action = fled ? "flee"
                             : (lastAction.includes("guard") || lastAction.includes("shield-bash") ||
                                lastAction.includes("ward")  || lastAction.includes("parry") ||
                                lastAction.includes("grace")) ? "guard"
                             : "strike";
                const entry = {
                  round:           (prev?.combat_round_counter || 0) + 1,
                  action,
                  dealt,
                  taken,
                  crit:            !!data?.last_crit,
                  enemy_hp_after:  nowEnemyHp,
                  enemy_max:       data?.enemy_max_hp || 0,
                  dead:            !!enemyKilled,
                };
                setCombatLog((old) => {
                  // If a fresh fight has started (different enemy alive now), reset the log.
                  if (data?.current_enemy && prev?.current_enemy && data.current_enemy !== prev.current_enemy) return [entry];
                  return [...old.slice(-9), entry];   // keep last 10
                });
                data.combat_round_counter = entry.round;
              } else if (data?.current_enemy && prev?.current_enemy && data.current_enemy !== prev.current_enemy) {
                // Fresh combat started with a different enemy — reset the log
                setCombatLog([]);
                data.combat_round_counter = 0;
              } else {
                data.combat_round_counter = prev?.combat_round_counter || 0;
              }
              return data;
            });
            setStreamingText("");
            // Contextual SFX — fires once per turn based on AI Director's beat
            try {
              playContextualSfxForTurn({
                musicTag:  data.music_tag,
                isDeath:   !!data.is_dead,
                isBoss:    !!data.is_boss_room,
                hpDelta:   data.last_hp_delta,
                goldDelta: data.last_gold_delta,
                xpDelta:   data.last_xp_delta,
                leveledUp: !!data.level_up,
                enemyName: data.enemy_name,
              });
            } catch (_e) { /* SFX must never break the turn */ }
            // Free the action buttons immediately — background scene_image (if any) will still arrive.
            setBusy(false);
            setThinking(false);
            // iter67.1 — Celebrate rare+ weapon/armor drops with a tier-colored toast.
            try {
              const drops = Array.isArray(data.last_items_gained) ? data.last_items_gained : [];
              drops.forEach((d) => {
                if (!d || !d.tier) return;
                const name = (d.name || d.item_id || "ITEM");
                if (d.tier === "legendary") {
                  toast.success(`⚡ LEGENDARY DROP · ${name}`, { duration: 5000 });
                } else if (d.tier === "epic") {
                  toast.success(`✦ EPIC DROP · ${name}`, { duration: 4000 });
                } else if (d.tier === "rare") {
                  toast.success(`◆ RARE DROP · ${name}`, { duration: 3000 });
                }
              });
            } catch (_e) { /* non-critical */ }
            // Boss Loot Chest — the backend flags `last_boss_kill_at` when a boss is killed.
            // Pop the modal exactly ONCE per boss kill (dedupe via lastBossKillSeen).
            if (data.last_boss_kill_at && data.last_boss_kill_at !== lastBossKillSeen && !data.is_dead) {
              setLastBossKillSeen(data.last_boss_kill_at);
              setLootChestOpen(true);
            }
            // iter67.5 — First-Death Rescue banner (fires once per user, backend-authored).
            if (data.first_death_rescued && data.first_death_rescue_message) {
              setRescueBanner(data.first_death_rescue_message);
              // iter70 — Defensive refresh. Backend NOW ships fresh rescue
              // choices inline, but if anything upstream ever drops them we
              // re-pull /game/state so the player never ends up with the
              // "Ember stirs" banner + no choice buttons (soft-lock).
              try { await refreshState(); } catch { /* non-fatal */ }
            }
            if (data.is_victory) {
              setVictory(data);
              await refreshState();
            } else if (data.is_dead) {
              // Death Cinematic — hold the fatal blow for ~2s before Game Over.
              setDeathCinematic({
                portraitUrl: run?.portrait_url || null,
                className:   run?.class_name || "Wanderer",
                pendingRun:  data,
              });
              const runFloor = data.floor || 0;
              const runKills = data.total_kills || data.kills || 0;
              const isBossFloor = runFloor === 5 || runFloor === 10 || data.is_boss_room;
              // Pre-compute which follow-up modal to open when the cinematic ends
              let followUp = null;
              if (isBossFloor) {
                followUp = "bossRevive";
              } else if (runFloor >= 3 || runKills >= 5) {
                try {
                  const spStatus = await api.get("/store/starter-pack/status");
                  followUp = spStatus?.eligible ? "starterPack" : "ashen";
                  if (followUp === "starterPack") {
                    setStarterPackContext({ floor: runFloor, kills: runKills });
                  }
                } catch {
                  followUp = "ashen";
                }
              }
              setDeathCinematic((c) => c ? { ...c, followUp } : c);
              await refreshState();
            }
          } else if (event === "error") {
            throw new Error(data.detail || "AI Director error");
          }
        }
      }
    } catch (e) {
      toast.error(e?.message || "Action failed");
      setStreamingText("");
    } finally {
      setBusy(false);
      setThinking(false);
    }
  };

  const handleLogout = async () => {
    await api.post("/auth/logout", {});
    navigate("/", { replace: true });
  };

  if (!state) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="font-heading text-2xl ash-text">Consulting the tome...</div>
      </div>
    );
  }

  const livesTotal = state.lives_remaining + (state.bonus_lives || 0);
  const showClassSelection = !run && !gameOver && !victory;

  const trackUrl = run?.current_music_url || MENU_TRACK;

  // stat_attack / stat_defense come precomputed from the backend based on equipped gear.
  const shownAttack = run?.stat_attack ?? 0;
  const shownDefense = run?.stat_defense ?? 0;

  const lvlCfg = state.level_config || { max_level: 100, xp_per_level: 50 };

  return (
    <div className="min-h-screen px-2 sm:px-4 md:px-8 lg:px-12 py-3 sm:py-6" data-testid="dashboard-page">
      {/* Top bar — wraps on mobile, single row on tablet+ */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-3 sm:mb-6">
        <div className="flex items-center gap-2 sm:gap-3">
          <div className="w-7 h-7 sm:w-8 sm:h-8 bg-[#d4af37] flex items-center justify-center text-black font-heading font-bold">E</div>
          <div>
            <div className="font-heading text-base sm:text-xl text-[#f0e6d2] leading-none">Dawn Vanguard</div>
            <div className="text-[9px] sm:text-[10px] tracking-[0.25em] sm:tracking-[0.3em] ash-text mt-0.5 sm:mt-1">ROGUE-LITE · CHAPTER I</div>
          </div>
        </div>
        <div className="flex items-center gap-1.5 sm:gap-2 flex-wrap justify-end">
          <AdaptiveAudio trackUrl={trackUrl} />
          <PushNotifyButton />
          <DailyEmberButton onClaimed={refreshState} />
          <div className="panel px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 sm:gap-2" data-testid="souls-header">
            <Flame size={14} className="ember" />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">SOULS</span>
            <span className="text-sm font-heading ember">{state.souls ?? 0}</span>
          </div>
          <button
            onClick={() => setTrophiesOpen(true)}
            className="panel px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 sm:gap-2 hover:border-[#d4af37] transition-colors"
            data-testid="header-trophies-btn"
            title="Ember Ledger — permanent trophies"
          >
            <Award size={14} className="gold-text" />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">TROPHIES</span>
          </button>
          <button
            onClick={() => setQuestsOpen(true)}
            className="panel px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 sm:gap-2 hover:border-[#d4af37] transition-colors"
            data-testid="header-quests-btn"
            title="Daily Pacts — earn Souls"
          >
            <Target size={14} className="gold-text" />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">PACTS</span>
          </button>
          <button
            onClick={() => setCodexOpen(true)}
            className="panel px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 sm:gap-2 hover:border-[#d4af37] transition-colors"
            data-testid="header-codex-btn"
            title="Codex — bestiary of foes slain"
          >
            <BookOpen size={14} className="ash-text" />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">CODEX</span>
          </button>
          <button
            onClick={() => navigate("/hall")}
            className="panel px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 sm:gap-2 hover:border-[#d4af37] transition-colors"
            data-testid="header-hall-btn"
          >
            <Trophy size={14} className="gold-text" />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">HALL</span>
          </button>
          <button
            onClick={() => setWellOpen(true)}
            className="panel px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 sm:gap-2 hover:border-[#e25822] transition-colors"
            data-testid="header-well-btn"
          >
            <Flame size={14} className="ember" />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">WELL</span>
          </button>
          <button
            onClick={() => setMetaShopOpen(true)}
            className="panel px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 sm:gap-2 hover:border-[#d4af37] transition-colors relative"
            data-testid="header-forge-btn"
            title="Forge of Ashes — permanent HP/Armor upgrades"
          >
            <Flame size={14} className="gold-text" />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">FORGE</span>
            {(state?.essence || 0) >= 10 && (
              <span
                className="absolute -top-1 -right-1 bg-[#d4af37] text-black text-[9px] font-bold rounded-full px-1.5 py-0.5 leading-none"
                data-testid="forge-badge"
              >
                !
              </span>
            )}
          </button>
          <button
            onClick={() => setStoreOpen(true)}
            className="panel px-2 py-1.5 sm:px-3 sm:py-2 flex items-center gap-1 sm:gap-2 hover:border-[#d4af37] transition-colors"
            data-testid="header-store-btn"
          >
            <ShoppingBag size={14} className="gold-text" />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">MERCHANT</span>
          </button>
          <button
            onClick={handleLogout}
            className="panel px-3 py-2 flex items-center gap-2 hover:border-[#8a0303] transition-colors"
            data-testid="logout-btn"
          >
            <LogOut size={14} />
            <span className="hidden sm:inline text-[10px] tracking-[0.3em] ash-text">EXIT</span>
          </button>
        </div>
      </div>

      <EventBanner />

      {victory ? (
        <VictoryScreen run={victory} onNewRun={() => { setVictory(null); setRun(null); }} />
      ) : gameOver ? (
        <GameOverScreen
          run={gameOver}
          reviveStreak={state?.monetag_revive_streak || 0}
          adFreeRevives={state?.ad_free_revives || 0}
          livesRemaining={livesTotal}
          onNewRun={() => { setGameOver(null); setRun(null); }}
          onOpenStore={() => setStoreOpen(true)}
          onAdRevived={async () => {
            setGameOver(null);
            setAshenOpen(false);
            await refreshState();
          }}
        />
      ) : showClassSelection ? (
        <>
          <div className="panel p-6 mb-6 flex items-center justify-between" data-testid="lives-indicator">
            <div>
              <div className="text-[10px] tracking-[0.3em] ash-text">DAILY EMBERS</div>
              <div className="font-heading text-3xl ember mt-1">
                {state.lives_remaining}<span className="text-lg ash-text">/{state.daily_max}</span>
                {state.bonus_lives > 0 && <span className="text-lg gold-text ml-2">+{state.bonus_lives}</span>}
              </div>
            </div>
            <div className="text-right">
              <div className="text-[10px] tracking-[0.3em] ash-text">SIGNED IN</div>
              <div className="text-sm text-[#f0e6d2] mt-1">{state.user.name}</div>
              <div className="text-xs ash-text">{state.user.email}</div>
            </div>
          </div>
          {livesTotal <= 0 ? (
            <>
              <div className="panel p-10 text-center" data-testid="no-lives-panel">
                <p className="text-xs tracking-[0.4em] blood-text">EMBERS EXTINGUISHED</p>
                <h3 className="font-heading text-4xl mt-2">Come back at dawn.</h3>
                <p className="ash-text mt-3">Your 3 daily lives will be restored tomorrow — or buy more from the Merchant.</p>
                <button onClick={() => setLivesModalOpen(true)} className="btn-primary mt-6" data-testid="empty-store-btn">
                  ⚔  BUY EMBERS NOW  ⚔
                </button>
              </div>
              <LivesExhaustedModal open={livesModalOpen} onClose={() => setLivesModalOpen(false)} />
            </>
          ) : (
            <ClassSelection
              classes={state.classes}
              onSelect={startRun}
              disabled={busy}
              floorGates={state.floor_gates || {}}
              bossFloors={state.boss_floors || [2, 4, 6, 8, 10]}
              finalFloor={state.final_floor || 10}
              playerLevel={1}
            />
          )}
        </>
      ) : run ? (
        <div className="grid grid-cols-1 lg:grid-cols-[1fr_320px] gap-3 sm:gap-6">
          <div className="space-y-3 sm:space-y-6">
            <PartyBar
              party={state.party}
              myUserId={state.user?.user_id}
              onInviteClick={() => window.location.assign(`/party/${state.party?.room_id}`)}
            />
            <ChapterBanner
              chapter={run.chapter}
              chapterTitle={run.chapter_title}
              floor={run.floor}
              finalFloor={run.final_floor || 10}
              isBossRoom={run.is_boss_room}
            />
            <DualMediaPanel
              sceneUrl={run.current_scene_url}
              portraitUrl={run.portrait_url}
              className={run.class_name}
              classKey={run.class_key}
              loadingScene={thinking && !run.current_scene_url}
              hitKey={hitKey}
              shieldUntil={run.revive_shield_until || null}
              health={run.health}
              maxHealth={run.max_health}
              equipped={run.equipped}
              itemsCatalog={state?.items_catalog}
            />
            <NarrativeBlock
              text={streamingText || run.current_narrative}
              loading={thinking && !streamingText}
              streaming={!!streamingText}
            />
            {run.current_enemy && (run.enemy_hp || 0) > 0 && (
              <CombatPanel
                enemyName={run.current_enemy}
                hp={run.enemy_hp || 0}
                maxHp={run.enemy_max_hp || 0}
                hpDelta={run.last_enemy_hp_delta || 0}
                isBoss={!!run.is_boss_room}
                strikeKey={strikeKey}
                portraitUrl={run.current_enemy_portrait_url ? absUrl(run.current_enemy_portrait_url) : ""}
                crit={!!run.last_crit}
              />
            )}
            {(run.current_enemy || combatLog.length > 0) && (
              <CombatLog entries={combatLog} />
            )}
            <TurnRewards
              turn={run.turn || 0}
              hpDelta={run.last_hp_delta || 0}
              goldDelta={run.last_gold_delta || 0}
              xpDelta={run.last_xp_delta || 0}
              levelUp={!!run.level_up}
              itemsGained={run.last_items_gained || []}
              crit={!!run.last_crit}
              lifesteal={run.last_lifesteal || 0}
              equippedWeaponId={run.equipped?.weapon || null}
              equippedWeaponAttack={run.weapon_attack || 0}
              onSwapped={(res) => {
                setRun((prev) => prev ? ({
                  ...prev,
                  equipped:     res.equipped,
                  stat_attack:  res.stat_attack,
                  stat_defense: res.stat_defense,
                  stat_crit_chance: res.stat_crit_chance,
                  stat_lifesteal:   res.stat_lifesteal,
                  stat_dmg_mult:    res.stat_dmg_mult,
                  weapon_tier:  res.weapon_tier,
                  active_sets:  res.active_sets || prev.active_sets || [],
                  // weapon_attack isn't returned explicitly — infer from stat_attack delta
                  weapon_attack: (prev.weapon_attack || 0) + (res.stat_attack - (prev.stat_attack || 0)),
                }) : prev);
              }}
            />
            {/* iter67.6 — Low-HP Potion Nudge — pulses when hp/max < 25% + potion in inventory */}
            <LowHpPotionNudge
              run={run}
              onUsed={(res) => {
                setRun((prev) => prev ? ({
                  ...prev,
                  health:    res.health ?? prev.health,
                  inventory: res.inventory ?? prev.inventory,
                }) : prev);
              }}
            />
            <ActionPanel
              choices={run.current_choices || []}
              onAction={takeAction}
              offlineMode={state.offline_mode}
              disabled={
                busy ||
                (state.party?.status === "playing" &&
                 state.party?.guest_user_id &&
                 state.party?.current_turn_user_id !== state.user?.user_id)
              }
            />
          </div>
          <div className="space-y-6">
            <StatsHUD
              health={run.health}
              maxHealth={run.max_health}
              gold={run.gold}
              floor={run.floor}
              livesRemaining={state.lives_remaining}
              dailyMax={state.daily_max}
              bonusLives={state.bonus_lives || 0}
              level={run.level || 1}
              xp={run.xp || 0}
              xpNeeded={run.xp_needed || lvlCfg.xp_per_level}
              maxLevel={lvlCfg.max_level}
              hpDelta={run.last_hp_delta || 0}
              goldDelta={run.last_gold_delta || 0}
              xpDelta={run.last_xp_delta || 0}
              attack={shownAttack}
              defense={shownDefense}
              critChance={run.stat_crit_chance || 0}
              lifesteal={run.stat_lifesteal || 0}
              dmgMult={run.stat_dmg_mult || 1.0}
              weaponTier={run.weapon_tier || "common"}
              enemyName={run.current_enemy || ""}
            />
            {run.level_up && (
              <div className="panel p-4 text-center border-[#d4af37]" data-testid="level-up-badge">
                <div className="font-heading text-2xl gold-text level-up">✧ LEVEL UP ✧</div>
                <div className="text-xs ash-text tracking-widest mt-1">Wounds mended. Frame steeled.</div>
              </div>
            )}
            <HpRestoreAdButton run={run} onRestored={(res) => {
              // Optimistically update local HP so the button hides right away.
              setRun((prev) => (prev ? { ...prev, health: res.new_health } : prev));
            }} />
            <ActivePerksBadge activePerks={run.active_perks || []} />
            <ActiveSetsBadge activeSets={run.active_sets || []} />
            <ComboMeter comboCount={run.combo_count || 0} />
            <button
              onClick={() => setShopOpen(true)}
              data-testid="open-shop-btn"
              className="panel w-full p-4 flex items-center justify-between hover:border-[#d4af37] transition-colors"
            >
              <div className="flex items-center gap-2">
                <Backpack size={16} className="gold-text" />
                <span className="font-heading text-lg text-[#f0e6d2]">Merchant & Pack</span>
              </div>
              <span className="text-xs ash-text tracking-widest flex items-center gap-1">
                <Coins size={12} className="gold-text" /> {run.gold}
              </span>
            </button>
            <div className="panel p-5">
              <div className="text-[10px] tracking-[0.3em] ash-text mb-3">— AMBIENCE —</div>
              <div className="font-heading text-xl arcane-text uppercase" data-testid="music-tag">
                {run.current_music_tag}
              </div>
              <p className="text-xs ash-text mt-2 italic">Tap the audio toggle in the header to hear the realm.</p>
            </div>
            <button
              onClick={async () => { await api.post("/game/abandon", {}); setRun(null); await refreshState(); }}
              className="btn-glow w-full"
              data-testid="abandon-btn"
            >
              ABANDON RUN
            </button>
          </div>
        </div>
      ) : null}

      <StoreModal open={storeOpen} onClose={() => setStoreOpen(false)} onPurchase={refreshState} />
      <ShopModal open={shopOpen} onClose={() => setShopOpen(false)} onChanged={refreshState} />
      <WellOfSoulsModal open={wellOpen} onClose={() => setWellOpen(false)} onChanged={refreshState} />
      <CodexModal open={codexOpen} onClose={() => setCodexOpen(false)} />
      <DailyQuestsModal open={questsOpen} onClose={() => setQuestsOpen(false)} onSoulsChanged={refreshState} />
      <AchievementsModal open={trophiesOpen} onClose={() => setTrophiesOpen(false)} />
      <AshenThresholdModal open={ashenOpen} run={gameOver} onClose={() => setAshenOpen(false)} />
      <StarterPackModal
        open={starterPackOpen}
        floor={starterPackContext.floor}
        kills={starterPackContext.kills}
        onClose={() => setStarterPackOpen(false)}
        onPurchased={() => setStarterPackOpen(false)}
      />
      <OnboardingOverlay
        open={onboardingOpen}
        onComplete={() => {
          setOnboardingOpen(false);
          // Optimistically flip local state so a rapid re-render doesn't briefly re-open it
          setState((s) => (s ? { ...s, tutorial_completed: true } : s));
        }}
      />
      <BossDefeatReviveModal
        open={bossDefeatOpen}
        run={gameOver}
        onDismiss={() => setBossDefeatOpen(false)}
        onRevived={async () => {
          setBossDefeatOpen(false);
          setGameOver(null);
          await refreshState();
        }}
      />
      <BossLootChestModal
        open={lootChestOpen}
        onClose={() => setLootChestOpen(false)}
        onClaimed={async () => {
          setLootChestOpen(false);
          await refreshState();
        }}
      />
      {bossCutin && (
        <BossCutin
          key={bossCutin.key}
          enemyName={bossCutin.name}
          enemyPortraitUrl={bossCutin.portrait}
          onDone={() => setBossCutin(null)}
        />
      )}
      <DeathCinematic
        open={!!deathCinematic}
        portraitUrl={deathCinematic?.portraitUrl}
        className={deathCinematic?.className}
        onComplete={() => {
          const d = deathCinematic;
          setDeathCinematic(null);
          if (!d) return;
          setGameOver(d.pendingRun);
          if (d.followUp === "bossRevive") setBossDefeatOpen(true);
          else if (d.followUp === "starterPack") setStarterPackOpen(true);
          else if (d.followUp === "ashen") setAshenOpen(true);
        }}
      />
      {trophyQueue.length > 0 && (
        <AchievementUnlockToast
          key={trophyQueue[0].achievement_id + trophyQueue[0].unlocked_at}
          trophy={trophyQueue[0]}
          onDone={() => setTrophyQueue((q) => q.slice(1))}
        />
      )}
      <EnrageOverlay
        enemyName={run?.enemy_name || run?.combat_state?.enemy_name}
        enemyHp={run?.enemy_hp ?? run?.combat_state?.enemy_hp}
        enemyMaxHp={run?.enemy_max_hp ?? run?.combat_state?.enemy_max_hp}
        isBoss={!!(run?.is_boss_room || run?.combat_state?.is_boss)}
      />
      <MetaShopModal
        open={metaShopOpen}
        onClose={() => setMetaShopOpen(false)}
        onPurchased={() => refreshState()}
      />
      <PerkDraftModal
        open={perkDraftOpen}
        perks={run?.pending_perks || []}
        onPicked={(res) => {
          setPerkDraftOpen(false);
          // Merge perk effects into local run so HUD / badges update instantly.
          setRun((prev) => prev ? ({
            ...prev,
            active_perks: res?.active_perks || prev.active_perks || [],
            max_health:   res?.max_health   || prev.max_health,
            health:       res?.health       || prev.health,
            pending_perks: [],
          }) : prev);
        }}
      />
      <BossPreviewCard
        open={!!bossPreview}
        preview={bossPreview}
        onClose={() => setBossPreview(null)}
        onOpenForge={() => setMetaShopOpen(true)}
      />
      <FirstDeathRescueBanner
        visible={!!rescueBanner}
        message={rescueBanner}
        onClose={() => setRescueBanner(null)}
      />
    </div>
  );
}
